"use client";

// Bandeau rouge global affiché sur l'app connectée tant qu'un paiement est en échec.
// "Payer maintenant" : s'il y a une seule facture impayée, on l'ouvre directement ;
// s'il y en a plusieurs, on envoie sur la page de régularisation (/pricing).

import { useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import { useRouter } from "@/i18n/navigation";
import { SpinnerCircular } from "spinners-react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

export default function PastDueBanner() {
    const { data: session, update } = useSession();
    const t = useTranslations();
    const router = useRouter();
    const [loading, setLoading] = useState(false);
    const [invoices, setInvoices] = useState(null); // null = pas encore su

    const isPastDue = !!session?.user?.paymentPastDue;

    // On charge les factures dès l'affichage du bandeau (utilisateurs impayés
    // uniquement) : ça adapte le message à l'engagement et accélère le clic.
    useEffect(() => {
        if (!isPastDue) return undefined;
        let active = true;
        fetch("/api/user/get/open-invoices")
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
            .then((d) => {
                if (!active) return;
                setInvoices(d.invoices || []);
                // Le serveur vient de rabattre un drapeau périmé : on relit la
                // session pour que tout le site le sache (verrou de /pricing compris).
                if (d.cleared) update();
            })
            .catch(() => {
                // Liste indisponible : le bandeau reste, le clic la redemandera.
            });
        return () => {
            active = false;
        };
        // Une fois par état d'impayé, pas à chaque relecture de la session
        // (l'identité de `update` change avec elle).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPastDue]);

    // Rien à régler, rien à annoncer : sans facture payable, le bandeau se tait,
    // même si le drapeau n'a pas encore pu être rabattu.
    if (!isPastDue || invoices?.length === 0) return null;

    const anyEngaged = (invoices || []).some((i) => i.engaged);

    const onPay = async () => {
        if (loading) return;
        setLoading(true);
        let list = invoices;
        if (!list) {
            try {
                const response = await fetch("/api/user/get/open-invoices");
                const data = await response.json();
                list = data.invoices || [];
            } catch (error) {
                list = [];
            }
        }
        if (list.length === 1 && list[0].hostedUrl) {
            window.location.href = list[0].hostedUrl;
        } else {
            router.push("/pricing");
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-3 bg-red-600 px-4 py-3 text-center text-sm font-medium leading-snug text-white sm:py-2.5">
            <span className="inline-flex items-center gap-2">
                <SolarIcon
                    name="danger-triangle-bold"
                    className="shrink-0 text-lg"
                />
                {anyEngaged
                    ? t("pastdue_banner_text_engaged")
                    : t("pastdue_banner_text")}
            </span>
            <button
                type="button"
                onClick={onPay}
                disabled={loading}
                className="inline-flex shrink-0 items-center gap-2 rounded-md bg-white px-4 py-2 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70"
            >
                {loading ? (
                    <SpinnerCircular
                        size={14}
                        color="#dc2626"
                        secondaryColor="#fecaca"
                    />
                ) : (
                    t("pastdue_banner_cta")
                )}
            </button>
        </div>
    );
}
